'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  BodyStrong,
  IconButton,
  MarkdownPreview,
  MetaDefault,
  HttpEditorCodeMirror,
  HttpEditorSearchBar,
  Tooltip,
  formatCategoryForDisplay,
  useThemeMode,
} from '@terra/ui-design-system';
import {
  Alert01Icon,
  Copy01Icon,
  Download01Icon,
  LinkSquare02Icon,
  File01Icon,
  Search01Icon,
} from '@terra/ui-icons';
import { terraProxyApiClient } from '../clients/testerClient';
import { useAppTargetId } from '../store/projectStore';
import { useChatStore } from '../store/chatStore';
import type { EndpointFile } from '@terra/shared-contracts-git';
import { createSourceCodeObjectContextMention } from '../utils/sourceCodeObjectContextMention';
import { SendToChatIconButton } from './SendToChatIconButton';
import { useCodeMirrorSendToChatToolbar } from './code/CodeMirrorSendToChatToolbar';
import {
  getLanguageLabelFromPath,
  toHttpBodyLanguage,
} from './code/fileContentViewerUtils';
import { useFileContentSearch } from './code/useFileContentSearch';

type FileContentViewerProps = {
  file?: EndpointFile;
  content?: string;
  isLoading?: boolean;
  onClose?: () => void;
};

export function FileContentViewer({
  file,
  content,
  isLoading = false,
  onClose,
}: FileContentViewerProps) {
  const appTargetId = useAppTargetId();
  const { mode } = useThemeMode();
  const tFileViewer = useTranslations('codeChanges.fileViewer');
  const { sendHttpMentionsToChat } = useChatStore();
  const [fileContent, setFileContent] = useState<string>('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const isFileSearchEnabled = file
    ? getLanguageLabelFromPath(file.filePath) !== 'markdown'
    : false;
  const {
    containerRef,
    editorRef,
    searchInputRef,
    showSearch,
    searchTerm,
    isRegex,
    isCaseSensitive,
    currentMatchIndex,
    matchCount,
    handleToggleSearch,
    closeSearch,
    handleSearchTermChange,
    handleNextMatch,
    handlePrevMatch,
    handleMatchCountChange,
    setIsRegex,
    setIsCaseSensitive,
  } = useFileContentSearch({
    fileId: file?.id,
    isSearchEnabled: isFileSearchEnabled,
  });

  useEffect(() => {
    let isStale = false;

    if (file && !content && appTargetId) {
      setIsLoadingContent(true);
      setFetchError(null);
      terraProxyApiClient
        .getFileContent(appTargetId, file.id)
        .then((result) => {
          if (isStale) {
            return;
          }

          setFileContent(result?.content || '');
          setFetchError(null);
        })
        .catch((error) => {
          if (isStale) {
            return;
          }

          console.warn('Failed to fetch file content:', error);
          setFileContent('');
          setFetchError(
            error instanceof Error
              ? error.message
              : tFileViewer('loadErrorFallback'),
          );
        })
        .finally(() => {
          if (isStale) {
            return;
          }

          setIsLoadingContent(false);
        });
    } else if (content) {
      setFileContent(content);
      setFetchError(null);
    }

    return () => {
      isStale = true;
    };
  }, [file, content, appTargetId, tFileViewer]);
  const displayContent = content || fileContent || '';
  const sendToCopilotLabel = tFileViewer('sendToCopilot');
  const searchInFileLabel = tFileViewer('searchInFile');
  const copyContentLabel = tFileViewer('copyContent');
  const downloadFileLabel = tFileViewer('downloadFile');
  const closeViewerLabel = tFileViewer('closeViewer');
  const sendSourceCodeToChat = useCallback(
    ({
      excerpt,
      lineStart,
      lineEnd,
    }: {
      excerpt?: string;
      lineStart?: number;
      lineEnd?: number;
    }) => {
      if (!file || !displayContent) {
        return;
      }

      const mention = createSourceCodeObjectContextMention({
        fileId: file.id,
        filePath: file.filePath,
        repositoryName: file.repositoryName,
        content: displayContent,
        selection: excerpt
          ? {
              excerpt,
              lineStart,
              lineEnd,
            }
          : undefined,
        labels: {
          attachedExcerptInstruction: tFileViewer('attachedExcerptInstruction'),
          fileLabel: (location) => tFileViewer('fileLabel', { location }),
          locationWithLines: ({ path, lineStart: start, lineEnd: end }) =>
            tFileViewer('locationWithLines', {
              path,
              lineStart: start,
              lineEnd: end,
            }),
        },
      });

      sendHttpMentionsToChat({
        mentions: [mention],
      });
    },
    [displayContent, file, sendHttpMentionsToChat, tFileViewer],
  );
  const { handleSelectionChange, selectionToolbar } =
    useCodeMirrorSendToChatToolbar({
      sendToChatLabel: sendToCopilotLabel,
      onSendSelection: ({ excerpt, lineStart, lineEnd }) => {
        sendSourceCodeToChat({ excerpt, lineStart, lineEnd });
      },
    });
  const handleSendEntireFile = useCallback(() => {
    sendSourceCodeToChat({});
  }, [sendSourceCodeToChat]);
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && showSearch) {
        event.preventDefault();
        closeSearch();
      }
    },
    [closeSearch, showSearch],
  );
  const searchOptions = useMemo(
    () => ({ isRegex, isCaseSensitive }),
    [isRegex, isCaseSensitive],
  );

  if (!file) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="3" alignItems="center">
          <File01Icon size={24} color="var(--colors-fg-muted)" />
          <VStack gap="1" alignItems="center">
            <BodyDefault color="fg.muted" textAlign="center">
              {tFileViewer('noFileSelected')}
            </BodyDefault>
            <MetaDefault color="fg.subtle" textAlign="center">
              {tFileViewer('selectFileHint')}
            </MetaDefault>
          </VStack>
        </VStack>
      </Box>
    );
  }

  const languageLabel = getLanguageLabelFromPath(file.filePath);
  const bodyLanguage = toHttpBodyLanguage(file.filePath);
  const isContentLoading = isLoading || isLoadingContent;
  const canSendToCopilot = Boolean(displayContent) && !isContentLoading;
  const lineCount = displayContent.split('\n').length;
  const nodeCategoryLabel =
    formatCategoryForDisplay(file.nodeCategory) ||
    tFileViewer('unknownCategory');
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayContent);
    } catch (error) {
      console.error('Failed to copy content:', error);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([displayContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = file.filePath.split('/').pop() || 'file.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      ref={containerRef}
      h="full"
      w="full"
      minH={0}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      outline="none"
    >
      <VStack gap="0" alignItems="stretch" h="full" w="full" minH={0}>
        <VStack
          gap="1"
          alignItems="stretch"
          p="3"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          flexShrink={0}
        >
          <HStack gap="2" alignItems="center" flex="1" minW="0">
            <Box flexShrink={0}>
              <File01Icon size={16} color="var(--colors-fg-muted)" />
            </Box>
            <BodyStrong truncate title={file.nodeName} flex="1" minW="0">
              {file.nodeName}
            </BodyStrong>
            {file.isEntryPoint && (
              <Badge variant="emphasis" size="sm" flexShrink={0}>
                {tFileViewer('attackSurfaceChanges')}
              </Badge>
            )}
            <HStack gap="1" flexShrink={0}>
              <SendToChatIconButton
                label={sendToCopilotLabel}
                onClick={handleSendEntireFile}
                disabled={!canSendToCopilot}
              />
              <Tooltip title={searchInFileLabel}>
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={handleToggleSearch}
                  aria-label={searchInFileLabel}
                  disabled={isContentLoading || !isFileSearchEnabled}
                >
                  <Search01Icon size={12} />
                </IconButton>
              </Tooltip>
              <Tooltip title={copyContentLabel}>
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={handleCopy}
                  aria-label={copyContentLabel}
                >
                  <Copy01Icon size={12} />
                </IconButton>
              </Tooltip>
              <Tooltip title={downloadFileLabel}>
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={handleDownload}
                  aria-label={downloadFileLabel}
                >
                  <Download01Icon size={12} />
                </IconButton>
              </Tooltip>
              {onClose && (
                <Tooltip title={closeViewerLabel}>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    onClick={onClose}
                    aria-label={closeViewerLabel}
                  >
                    <LinkSquare02Icon size={12} />
                  </IconButton>
                </Tooltip>
              )}
            </HStack>
          </HStack>

          <MetaDefault
            color="fg.muted"
            truncate
            title={`${file.repositoryName}/${file.filePath}${file.startLine && file.endLine ? ` (${file.startLine}-${file.endLine})` : ''}`}
          >
            {file.repositoryName}/{file.filePath}
            {file.startLine && file.endLine && (
              <MetaDefault as="span" color="fg.subtle">
                {' '}
                ({file.startLine}-{file.endLine})
              </MetaDefault>
            )}
          </MetaDefault>
        </VStack>

        <Box flex="1" minH={0} overflow="hidden" w="full" position="relative">
          {isContentLoading ? (
            <Box
              h="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <BodyDefault color="fg.muted">
                {tFileViewer('loadingContent')}
              </BodyDefault>
            </Box>
          ) : fetchError ? (
            <Box
              h="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack gap="3" alignItems="center" maxW="md" p="4">
                <Alert01Icon size={24} color="var(--colors-fg-error)" />
                <VStack gap="1" alignItems="center">
                  <BodyStrong color="fg.error" textAlign="center">
                    {tFileViewer('loadErrorTitle')}
                  </BodyStrong>
                  <MetaDefault color="fg.muted" textAlign="center">
                    {fetchError}
                  </MetaDefault>
                </VStack>
              </VStack>
            </Box>
          ) : languageLabel === 'markdown' ? (
            <Box h="full" overflow="auto">
              <MarkdownPreview source={displayContent} />
            </Box>
          ) : (
            <HttpEditorCodeMirror
              ref={editorRef}
              value={displayContent}
              readOnly
              showLineNumbers
              wordWrap
              theme={mode === 'dark' ? 'dark' : 'light'}
              languageOverride={bodyLanguage}
              transparentBackground
              syntaxPalette="ide"
              height="100%"
              onSelectionChange={handleSelectionChange}
              searchTerm={showSearch ? searchTerm : undefined}
              searchOptions={searchOptions}
              currentMatchIndex={currentMatchIndex}
              onMatchCountChange={handleMatchCountChange}
            />
          )}
          {selectionToolbar}
        </Box>

        {showSearch && isFileSearchEnabled && (
          <HttpEditorSearchBar
            searchTerm={searchTerm}
            onSearchTermChange={handleSearchTermChange}
            isRegex={isRegex}
            onRegexToggle={() => setIsRegex((previous) => !previous)}
            isCaseSensitive={isCaseSensitive}
            onCaseSensitiveToggle={() =>
              setIsCaseSensitive((previous) => !previous)
            }
            currentMatchIndex={currentMatchIndex}
            matchCount={matchCount}
            onNextMatch={handleNextMatch}
            onPrevMatch={handlePrevMatch}
            onClose={handleToggleSearch}
            searchInputRef={searchInputRef}
            hideReplace
          />
        )}

        <HStack
          justifyContent="space-between"
          alignItems="center"
          p="2"
          borderTopWidth="1px"
          borderColor="border.subtle"
        >
          <MetaDefault color="fg.muted">
            {tFileViewer('languageLineCount', {
              language: languageLabel,
              lineCount: tFileViewer('lineCount', { count: lineCount }),
            })}
          </MetaDefault>
          <Badge variant="outline" size="sm">
            {nodeCategoryLabel}
          </Badge>
        </HStack>
      </VStack>
    </Box>
  );
}
